import { useEffect, useState, useRef, ReactNode } from 'react'

import { useQuery, useReactiveVar } from '@apollo/client'
import Flicking from '@egjs/react-flicking'
import { useRouter } from 'next/router'

import { CommonClose3RoundIcon } from '@local/assets/icons'
import { isEmptyArray } from '@local/utils'

import Clickable from '@web/components/Clickable'
import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import Image, { PupKeywordEmoji } from '@web/components/Image'
import { modalListVar } from '@web/components/Modal/GlobalModal/globalModal.helper'
import { gql } from '@web/gql'
import { MissionRewardPopup_MissionFragment, MissionGroupType } from '@web/gql/graphql'
import useLockBodyScroll from '@web/hooks/useLockBodyScroll'
import useMe from '@web/hooks/useMe'
import useNow from '@web/hooks/useNow'
import { apolloClient } from '@web/libs/apollo/client'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import routes from '@web/libs/constants/routes'
import { isMatchPath } from '@web/libs/matchPath'
import nClicks from '@web/libs/nlog/click/codes'
import { startSequence, useRegistToSequentialList } from '@web/libs/sequential'
import { browserStorage, isNotNullish } from '@web/libs/utils'

import { MissionRewardPopupType } from './MissionRewardPopup.enum'
import styles from './MissionRewardPopup.module.scss'
import NPayRewardPopup from './MissionRewardPopupItem/NPayRewardPopup'
import PhotoReviewRewardPopup from './MissionRewardPopupItem/PhotoReviewRewardPopup'
import ProfileBorderRewardPopup from './MissionRewardPopupItem/ProfileBorderRewardPopup'
import ReceiptCollectorRewardPopup from './MissionRewardPopupItem/ReceiptCollectorRewardPopup'
import RegionReviewRewardPopup from './MissionRewardPopupItem/RegionReviewRewardPopup'

const query = gql(`
query MissionRewardPopup($yearMonth: YearMonth!) {
  missionGroups(yearMonth: $yearMonth) {
    ... on MissionGroupsSucceed {
      missionGroups {
        ...MissionCard_MissionGroup
      }
    }
  }

  lastMonthWinMission {
    ...MissionRewardPopup_Mission
  }
}

fragment MissionRewardPopup_Mission on Mission {
  id
  name
  reward {
    ... on NPayMissionReward {
      amount
    }
    ... on ProfileBorderMissionReward {
      decoration {
        name
      }
    }
  }
}
`)

const MissionRewardPopup = () => {
  const router = useRouter()

  const isPathShowable = isMatchPath(router.pathname, {
    path: [
      routes.MISSION,
      routes.MISSION_HISTORY,
      routes.HOME_MY_REVIEW,
      routes.HOME_REVIEW,
      routes.REVIEW_DETAIL,
      routes.REVIEW_FEED,
    ],
    exact: true,
  })

  const today = useNow()
  const [displayMissions, setDisplayMissions] = useState<MissionRewardPopup_MissionFragment[]>([])
  const [display, setDisplay] = useState<boolean>(false)

  const showingModalList = useReactiveVar(modalListVar).filter(modal => modal.isShow)

  const isOtherModalShow = showingModalList.length > 0

  const { data, loading } = useQuery(query, {
    variables: { yearMonth: today.format('YYYY-MM') },
    skip: !isPathShowable,
    client: apolloClient,
  })
  const { me } = useMe()
  const flickingRef = useRef<Flicking>(null)

  useLockBodyScroll({ needLock: display })

  const getRewardedMissions = () => {
    const { MissionGroupsSucceed } = extractByTypename(data?.missionGroups)

    const borderMissions =
      MissionGroupsSucceed?.missionGroups
        .filter(missionGroup => {
          return (
            missionGroup.type === MissionGroupType.ProfileBorder &&
            missionGroup.missionActivity?.missionStates?.[0]?.complete
          )
        })
        .map(missionGroup => {
          const { SingleMissionGroup } = extractByTypename(missionGroup)

          return SingleMissionGroup?.mission
        }) ?? []

    const lastMonthWinMission = data?.lastMonthWinMission ?? []

    return [...borderMissions, ...lastMonthWinMission].filter(isNotNullish)
  }

  useEffect(() => {
    if (isOtherModalShow) {
      setDisplay(false)

      return
    }

    if (isEmptyArray(displayMissions)) {
      setDisplay(false)

      return
    }

    setDisplay(true)
  }, [isOtherModalShow, displayMissions])

  useEffect(() => {
    if (loading) return

    const rewardedMissions = getRewardedMissions()

    const displayMissions = rewardedMissions.filter(mission => {
      const openMissionRewardPopupStorage = browserStorage('openMissionRewardPopup', {
        userId: me?.id ?? 'no_login',
        missionId: mission.id,
      })

      const show = !openMissionRewardPopupStorage.get()

      if (show) {
        openMissionRewardPopupStorage.set('true')
      }

      return show
    })

    setDisplayMissions(displayMissions)
  }, [data, loading])

  useRegistToSequentialList(
    displayMissions.map((_, index) => {
      if (index === 0)
        return {
          order: 0,
          type: 'afterPrev',
          onExecute: () => null,
          delaySeconds: 0,
          durationSeconds: 0,
          id: `flicking-move-to-${index}`,
        }

      return {
        order: index * 100 - 50,
        onExecute: () => flickingRef.current?.moveTo(index).catch(() => null),
        type: 'afterPrev',
        delaySeconds: 0,
        durationSeconds: 0.5,
        id: `flicking-move-to-${index}`,
      }
    }),
  )

  useEffect(() => {
    if (!display) return () => {}

    let startTimeout: number
    startTimeout = window.setTimeout(() => startSequence(true), 300)

    return () => {
      if (startTimeout) window.clearTimeout(startTimeout)
    }
  }, [display])

  if (!display) return null

  const handleClickClose = () => setDisplay(false)

  const paginationType = displayMissions.length > 1 ? 'bullet' : undefined

  return (
    <div className={styles.mission_area}>
      <FlickingArea
        className={styles.flicking_area}
        paginationType={paginationType}
        options={{ moveType: 'snap', bound: false }}
        ref={flickingRef}
      >
        {displayMissions.map((mission, index) => {
          let Popup: ReactNode = null

          const { NPayMissionReward, ProfileBorderMissionReward } = extractByTypename(mission.reward)

          if (ProfileBorderMissionReward)
            Popup = (
              <ProfileBorderRewardPopup
                animationOrder={index}
                title={mission.name}
                borderName={ProfileBorderMissionReward.decoration.name}
              />
            )

          if (NPayMissionReward) {
            if (mission.name === MissionRewardPopupType.PHOTO_REVIEW)
              Popup = (
                <PhotoReviewRewardPopup title={mission.name} amount={NPayMissionReward.amount} animationOrder={index} />
              )
            else if (mission.name === MissionRewardPopupType.REGION_REVIEW)
              Popup = (
                <RegionReviewRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                />
              )
            else if (
              mission.name === MissionRewardPopupType.RECEIPT_COLLECTOR ||
              mission.name == MissionRewardPopupType.RECEIPT_COLLECTOR_2
            )
              Popup = (
                <ReceiptCollectorRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                />
              )
            else if (mission.name === MissionRewardPopupType.VIDEO_REVIEW) {
              Popup = (
                <NPayRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                  description={
                    <>
                      생생한 영상 리뷰를 남겨주셨네요!
                      <br />
                      당첨을 축하합니다
                      <Image className={styles.icon} src={PupKeywordEmoji.PARTY_POPPER} width={18} height={18} alt="" />
                    </>
                  }
                  emoji={PupKeywordEmoji.FIRE}
                />
              )
            } else if (
              [
                MissionRewardPopupType.NATURE_REVIEW,
                MissionRewardPopupType.SUMMER_NATURE_REVIEW,
                MissionRewardPopupType.AUTUMN_NATURE_REVIEW,
                MissionRewardPopupType.WINTER_NATURE_REVIEW,
                MissionRewardPopupType.SPRING_NATURE_REVIEW,
              ].includes(mission.name as MissionRewardPopupType)
            ) {
              Popup = (
                <NPayRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                  description={
                    <>
                      자연, 지역명소를 방문하는 분들께 도움을 주셨어요!
                      <br />
                      당첨을 축하합니다
                      <Image className={styles.icon} src={PupKeywordEmoji.PARTY_POPPER} width={18} height={18} alt="" />
                    </>
                  }
                />
              )
            } else if ([MissionRewardPopupType.REGION_REVIEW_STEP1].includes(mission.name as MissionRewardPopupType)) {
              Popup = (
                <NPayRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                  description={
                    <>
                      이 지역을 방문하는 분들께 도움을 주셨어요!
                      <br />
                      당첨을 축하합니다
                      <Image className={styles.icon} src={PupKeywordEmoji.PARTY_POPPER} width={18} height={18} alt="" />
                    </>
                  }
                />
              )
            } else if (
              [MissionRewardPopupType.PLACE_CATEGORY_REVIEW].includes(mission.name as MissionRewardPopupType)
            ) {
              Popup = (
                <NPayRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                  description={
                    <>
                      맛집을 방문하는 분들께 도움을 주셨어요!
                      <br />
                      당첨을 축하합니다
                      <Image className={styles.icon} src={PupKeywordEmoji.PARTY_POPPER} width={18} height={18} alt="" />
                    </>
                  }
                />
              )
            } else {
              Popup = (
                <NPayRewardPopup
                  title={mission.name}
                  amount={NPayMissionReward.amount}
                  animationOrder={index}
                  emoji={PupKeywordEmoji.FIRE}
                />
              )
            }
          }

          return (
            <div className={styles.inner} key={mission.id}>
              {Popup}
            </div>
          )
        })}
      </FlickingArea>
      <Clickable className={styles.btn_close} onClick={handleClickClose} nClickCode={nClicks.mission.REWARD_POPUPX}>
        <span className="place_blind">닫기</span>
        <CommonClose3RoundIcon className={styles.icon} aria-hidden="true" />
      </Clickable>
    </div>
  )
}

export default MissionRewardPopup
